import {
  BLOCK_REASONS,
  CHECK_CAUSES,
  EXPLORER_INVALID_REQUEST_REASONS,
  LOCATION_SOURCE_UNAVAILABLE_CAUSES,
  REASON_CATALOG,
  REASON_CATEGORIES,
  isCheckCause,
  isExplorerInvalidRequestReason,
  isLocationSourceUnavailableCause,
} from "@strust/contracts";
import type {
  BlockReason,
  ReasonCatalogEntry,
  ReasonCategory,
  ReasonCode,
} from "@strust/contracts";

/**
 * Message namespaces for the stable codes the API returns (design D16). The API never translates:
 * the SPA maps each code to a message id. The catalogs are checked against `@strust/contracts` by
 * `pnpm --filter @strust/hub i18n:check`.
 */

/** `decision.allow` = Approved; anything else, including unknown future values, is Denied. */
export function decisionMessageId(decision: string): "decision.allow" | "decision.deny" {
  return decision === "allow" ? "decision.allow" : "decision.deny";
}

export interface ReasonMessageIds {
  /** Player-facing message (the catalog's `playerMessageId`); `undefined` when none is shown. */
  readonly player: string | undefined;
  readonly title: string;
  readonly description: string;
  readonly category: string;
}

export function isReasonCode(code: string): code is ReasonCode {
  return Object.hasOwn(REASON_CATALOG, code);
}

/** Message ids of a verification reason, or `undefined` for a code this build does not know. */
export function reasonMessageIds(code: string): ReasonMessageIds | undefined {
  if (!isReasonCode(code)) return undefined;
  const entry = REASON_CATALOG[code];
  return {
    player: entry.playerMessageId ?? undefined,
    title: `reason.${code}.title`,
    description: `reason.${code}.description`,
    category: reasonCategoryMessageId(entry.category),
  };
}

export function reasonCategoryMessageId(category: ReasonCategory): string {
  return `reasonCategory.${category}`;
}

/** Block reasons are their own namespace, never a verification reason. */
export function blockReasonMessageId(reason: BlockReason): string {
  return `blockReason.${reason}`;
}

/** Message id of why a location source has no location, or `undefined` for an unknown cause. */
export function sourceCauseMessageId(cause: string): string | undefined {
  return isLocationSourceUnavailableCause(cause) ? `sourceCause.${cause}` : undefined;
}

/** Message id of a check cause that is not a verification reason (`checkCause.*`). */
export function checkCauseMessageId(cause: string): string | undefined {
  return isCheckCause(cause) ? `checkCause.${cause}` : undefined;
}

/** Message id of a domain reason inside an `invalid_request` answer. */
export function invalidRequestReasonMessageId(reason: string): string | undefined {
  return isExplorerInvalidRequestReason(reason) ? `invalidRequestReason.${reason}` : undefined;
}

/** Every message id the namespaces must define, derived from the contracts catalogs. */
export function expectedNamespaceIds(): readonly string[] {
  return [
    "decision.allow",
    "decision.deny",
    ...Object.entries(REASON_CATALOG).flatMap(([code, entry]) => [
      ...(entry.playerMessageId === null ? [] : [entry.playerMessageId]),
      `reason.${code}.title`,
      `reason.${code}.description`,
    ]),
    ...REASON_CATEGORIES.map(reasonCategoryMessageId),
    ...BLOCK_REASONS.map(blockReasonMessageId),
    ...LOCATION_SOURCE_UNAVAILABLE_CAUSES.map((c) => `sourceCause.${c}`),
    ...CHECK_CAUSES.map((c) => `checkCause.${c}`),
    ...EXPLORER_INVALID_REQUEST_REASONS.map((r) => `invalidRequestReason.${r}`),
  ];
}

/** A message whose text is owned by `@strust/contracts`, not authored in the hub catalogs. */
export interface ContractText {
  readonly en: string;
  readonly ptBR: string;
}

/**
 * Texts the catalogs must copy verbatim from the contracts: the player message of each reason
 * (`REASON_CATALOG[code].playerMessage`, the same text the integration docs publish, D16).
 */
export function contractTexts(): Readonly<Record<string, ContractText>> {
  return Object.fromEntries(
    Object.values<ReasonCatalogEntry>(REASON_CATALOG).flatMap((entry) =>
      entry.playerMessageId === null || entry.playerMessage === null
        ? []
        : [
            [
              entry.playerMessageId,
              { en: entry.playerMessage.en, ptBR: entry.playerMessage["pt-BR"] },
            ],
          ],
    ),
  );
}

/** Prefixes owned by the generated namespaces: stale keys under them fail the check. */
export const NAMESPACE_PREFIXES = [
  "decision.",
  "reason.",
  "reasonCategory.",
  "blockReason.",
  "sourceCause.",
  "checkCause.",
  "invalidRequestReason.",
] as const;
